import axios from "axios";
import moment from "moment";
import config from "../config/config";

const RESERVATIONS_PER_PAGE = 10;
const PAYMENTS_PER_PAGE = 10;

// Globalny interceptor: backend ustawia header X-Token-Status: EXPIRED/INVALID
// gdy przyjdzie z wygasłym/popsutym JWT (publiczne endpointy nadal odpowiadają).
// Czyścimy lokalnie token, żeby UI przeszedł w tryb anonimowy bez „zawieszania się".
// Status 401 traktujemy tak samo (chroniony endpoint, brak ważnej autoryzacji).
const handleTokenInvalidation = (headers, status) => {
    const tokenStatus = headers?.['x-token-status'] || headers?.['X-Token-Status'];
    const isAuthFailure = status === 401 || status === 403;
    if (tokenStatus === 'EXPIRED' || tokenStatus === 'INVALID' || isAuthFailure) {
        if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
        }
    }
};

axios.interceptors.response.use(
    (response) => {
        handleTokenInvalidation(response.headers, response.status);
        return response;
    },
    (error) => {
        handleTokenInvalidation(error.response?.headers, error.response?.status);
        return Promise.reject(error);
    }
);


export const deleteExpiredTokens = async () => {
    try {
        const response = await axios.delete(`${config.apiUrl}/super/confirmation-token/expired`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateUserByAdmin = async (id, updates) => {
    const response = await fetch(`${config.apiUrl}/admin/user/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updates),
    });

    if (!response.ok) {
        throw new Error('Błąd podczas aktualizacji danych użytkownika przez administratora.');
    }

    return await response.json();
};


export const togglePayingInProgress = async (id) => {
    const response = await fetch(`${config.apiUrl}/admin/reservations/togglePaying/${id}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
    });

    if (!response.ok) {
        throw new Error('Błąd podczas aktualizacji danych użytkownika przez administratora.');
    }

    return await response.json();
};

export const updateUserBySuper = async (id, updates) => {
    const response = await fetch(`${config.apiUrl}/super/user/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updates),
    });

    if (!response.ok) {
        throw new Error('Błąd podczas aktualizacji danych użytkownika przez administratora.');
    }

    return await response.json();
};

export const fetchUsers = async () => {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/admin/user/all`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export async function sendEmailToUsers(recipients, subject, message) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/admin/users/send-email`,
            { recipients, subject, message },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export const deleteUser = async (id) => {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/super/user/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export async function updateNews(id, newsData) {
    try {

        const token = getToken();

        const response = await axios.put(`${config.apiUrl}/admin/news/update/${id}`, newsData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function deleteNews(id) {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/admin/news/delete/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchH2H(matchId) {
    try {
        const response = await axios.get(`${config.apiUrl}/matches/${matchId}/h2h`);
        return response.data;
    } catch (error) {
        console.error('Błąd pobierania statystyk H2H', error);
        return [];
    }
}


export async function fetchNews() {
    try {
        const response = await axios.get(`${config.apiUrl}/news`);
        return response.data;
    } catch (error) {
        return [];
    }
}

export async function createNews(newsData) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/admin/news/add`, newsData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function loadReservations(page = 0, reservationType) {
    try {
        const skip = page * RESERVATIONS_PER_PAGE;
        return await fetchUserReservations(reservationType, null, skip, RESERVATIONS_PER_PAGE);
    } catch (error) {
        throw error;
    }
}

export async function loadAllReservations(page = 0, reservationType) {
    try {
        const skip = page * RESERVATIONS_PER_PAGE;
        return await fetchAllUserReservations(reservationType, null, skip, RESERVATIONS_PER_PAGE);
    } catch (error) {
        return [];
    }
}

export function redirectToLoginIfInvalidToken(navigate, from) {
    const token = getToken();
    if (!token || !isTokenValid(token)) {
        console.log("Invalid or missing token. Redirecting to login.", "from: " + from);
        localStorage.removeItem("token");
        navigate("/login", {state: {from}});
        return false;
    }
    return true;
}

export function getToken() {
    const token = localStorage.getItem("token");
    if (!token) return null;
    if (!isTokenValid(token)) {
        // Lokalna walidacja przed wysyłką — wygasły/popsuty token nie trafia do backendu,
        // a UI od razu zachowuje się jak dla anonima zamiast wywoływać 401-na-401.
        localStorage.removeItem("token");
        return null;
    }
    return token;
}

export function isTokenValid(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        // Check if the token's expiration time (exp) is greater than the current time
        return payload.exp > currentTime;
    } catch (error) {
        return false;
    }
}

export async function getUserData() {
    try {
        const token = getToken();


        if (!token || !isTokenValid(token)) {
            return {};
        }

        const response = await axios.get(`${config.apiUrl}/user/profile`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchAccessCodeStatus() {
    try {
        const token = getToken();
        if (!token || !isTokenValid(token)) return {active: false};
        const response = await axios.get(`${config.apiUrl}/user/access-code/status`, {
            headers: {Authorization: `Bearer ${token}`},
        });
        return response.data;
    } catch (error) {
        return {active: false};
    }
}

export async function fetchActiveCoaches() {
    try {

        const response = await axios.get(`${config.apiUrl}/active-coaches`);
        return response.data;
    } catch (error) {
        return [];
    }
}


export async function updateUserData(updatedData) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return false;
        }

        await axios.put(`${config.apiUrl}/user/profile`, updatedData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return true;
    } catch (error) {
        throw error
        // return false;
    }
}

export async function updateReservationByAdmin(updatedData) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return false;
        }

        await axios.patch(`${config.apiUrl}/admin/reservation/edit`, updatedData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return true;
    } catch (error) {
        throw error
        // return false;
    }
}

export async function fetchReservations(selectedDate, navigate) {
    try {
        const dateString = selectedDate.toISOString().split("T")[0];
        const token = getToken();

        const headers = token && isTokenValid(token)
            ? {Authorization: `Bearer ${token}`}
            : {};

        const response = await axios.get(`${config.apiUrl}/reservations?date=${dateString}`, {
            headers,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchReservationsByAdmin(selectedDate, navigate) {
    try {
        const dateString = selectedDate.toISOString().split("T")[0];
        const token = getToken();

        if (!redirectToLoginIfInvalidToken(navigate)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/view/reservations?date=${dateString}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function updateUsersWithPayedToDate(selectedDate) {
    try {
        const dateString = selectedDate.toISOString().split("T")[0];
        const token = getToken();


        const response = await axios.patch(
            `${config.apiUrl}/view/updatePayedToDate/users?date=${dateString}`,
            null, // tu można przekazać ciało żądania, jeśli jest potrzebne
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error updating users' payedToDate:", error);
        return 0;
    }
}

export async function loadAllPaymentsByAdmin(page = 0) {
    try {
        const skip = page;
        return await fetchAllAdminPayments(skip, PAYMENTS_PER_PAGE);
    } catch (error) {
        return [];
    }
}

export async function fetchAllAdminPayments(skip, limit) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/admin/payments`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                skip: skip,
                limit: limit,
            },
        });
        return response.data;
    } catch (error) {
        return [];
    }
}

export async function loadAllPaidPayments(page = 0) {
    try {
        const skip = page;
        return await fetchPaidPayments(skip, PAYMENTS_PER_PAGE);
    } catch (error) {
        return [];
    }
}


export async function fetchPaidPayments(skip, limit) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/payments/user/paid`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                skip: skip,
                limit: limit,
            },
        });
        return response.data;
    } catch (error) {
        return [];
    }
}


export async function fetchUnpaidReservations() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/reservations/user/unpaid`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}


export async function fetchUnpaidFees() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/payments/user/unpaid`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchReservationsForPayment() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/reservations/user/reservations-for-payment`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchAllUnpaidReservations() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/admin/reservations/unpaid`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchAllUnpaidFees() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/admin/payments/unpaid`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        return [];
    }
}

export async function markReservationAsPaid(reservationId) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return null;
        }

        const response = await axios.patch(
            `${config.apiUrl}/admin/reservations/${reservationId}/markAsPaid`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        return null;
    }
}

export async function cancelPaymentByAdmin(paymentId) {
    try {
        const response = await axios.patch(
            `${config.apiUrl}/admin/payments/${paymentId}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function createPaymentByAdmin({ email, title, status, amount }) {
    try {
        const response = await axios.post(
            `${config.apiUrl}/admin/payments/create`,
            { email, title, status, amount },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function markPaymentAsPaid(paymentId) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return null;
        }

        const response = await axios.patch(
            `${config.apiUrl}/admin/payments/${paymentId}/markAsPaid`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        return null;
    }
}

export async function fetchUserReservations(
    reservationType = "current",
    navigate,
    skip,
    limit
) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            if (navigate) {
                navigate("/login", {replace: true});
            }
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/reservations/user`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                type: reservationType,
                skip: skip,
                limit: limit,
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function fetchAllUserReservations(
    reservationType = "current",
    navigate,
    skip,
    limit
) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            if (navigate) {
                navigate("/login", {replace: true});
            }
            return [];
        }

        const response = await axios.get(`${config.apiUrl}/admin/reservations/user`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                type: reservationType,
                skip: skip,
                limit: limit,
            },
        });
        return response.data;
    } catch (error) {
        return [];
    }
}


export async function fetchReservationCount(reservationType) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return 0;
        }

        const response = await axios.get(`${config.apiUrl}/reservations/user/count`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                type: reservationType,
            },
        });
        return response.data.count;
    } catch (error) {
        return 0;
    }
}

export async function fetchAllReservationCount(reservationType) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return 0;
        }

        const response = await axios.get(`${config.apiUrl}/admin/reservations/user/count`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                type: reservationType,
            },
        });
        return response.data.count;
    } catch (error) {
        return 0;
    }
}

export async function fetchAllPaymentsCount() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return 0;
        }

        const response = await axios.get(`${config.apiUrl}/admin/payments/count`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data.count;
    } catch (error) {
        return 0;
    }
}

export async function fetchAllPaymentsPaidCount() {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return 0;
        }

        const response = await axios.get(`${config.apiUrl}/user/payments/paid/count`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data.count;
    } catch (error) {
        return 0;
    }
}

export async function fetchCourts(navigate) {
    try {
        const token = getToken();
        const headers = token && isTokenValid(token)
            ? {Authorization: `Bearer ${token}`}
            : {};

        const response = await axios.get(`${config.apiUrl}/courts`, {
            headers,
        });
        return response.data;
    } catch (error) {
        return [];
    }
}

export async function createReservation(reservationData) {
    try {
        const token = getToken();

        if (!token || !isTokenValid(token)) {
            return [];
        }

        const response = await axios.post(`${config.apiUrl}/reservations`, reservationData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }

}

export async function cancelReservation(reservationId) {
    try {
        const token = getToken();
        const response = await axios.patch(
            `${config.apiUrl}/reservations/cancel/${reservationId}`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return {success: true, data: response.data};
    } catch (error) {
        throw error;
    }
}

export async function initiatePaymentOnServer(payment) {
    try {
        const token = getToken();
        const response = await axios.post(
            `${config.apiUrl}/payments/initiate`,
            payment,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function generateAccessCode(userId) {
    try {
        const token = getToken();
        const response = await axios.put(
            `${config.apiUrl}/admin/user/${userId}/generate-access-code`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Błąd podczas generowania kodu:', error);
        throw error;
    }
}

export async function fetchAccessLogs(daysBack = 7, userId) {
    try {
        const token = getToken();
        const params = new URLSearchParams({ daysBack: String(daysBack) });
        if (userId) params.append('userId', String(userId));

        const response = await axios.get(
            `${config.apiUrl}/api/access/logs?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    } catch (error) {
        console.error('Błąd podczas pobierania logów dostępu:', error);
        throw error;
    }
}




export async function cancelAdminReservation(reservationId, sendEmail, emailMessage) {
    try {
        const token = getToken();
        const response = await axios.patch(
            `${config.apiUrl}/admin/reservations/cancel/${reservationId}`,
            {
                sendEmail,
                emailMessage,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return {success: true, data: response.data};
    } catch (error) {
        throw error;
    }
}


export async function cancelAdminReservationsAfter(selectedDate, startTime) {
    try {
        const token = getToken();
        const formattedSelectedDate = new Date(selectedDate).toISOString().split('T')[0];
        const startTimeDate = new Date();
        const hours = String(startTimeDate.getHours()).padStart(2, '0');
        const minutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const formattedStartTime = startTime || `${hours}:${minutes}:00`;

        const response = await axios.patch(
            `${config.apiUrl}/view/reservations/cancel-after?selectedDate=${formattedSelectedDate}&startTime=${formattedStartTime}`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return {success: true, data: response.data};
    } catch (error) {
        throw error;
    }
}

function roundToNearestHalfHour(date) {
    const coeff = 1000 * 60 * 30;
    return new Date(Math.round(date.getTime() / coeff) * coeff);
}

export async function blockAdminAllFreeCells(selectedDate, startTime) {
    try {
        const token = getToken();
        const formattedSelectedDate = new Date(selectedDate).toISOString().split('T')[0];
        const now = new Date();
        const startTimeDate = roundToNearestHalfHour(new Date(`${now.toISOString().split("T")[0]}T${startTime}`));
        const hours = String(startTimeDate.getHours()).padStart(2, '0');
        const minutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const formattedStartTime = `${hours}:${minutes}:00`;

        const response = await axios.post(
            `${config.apiUrl}/view/reservations/block-free-cells?selectedDate=${formattedSelectedDate}&startTime=${formattedStartTime}`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return {success: true, data: response.data};
    } catch (error) {
        throw error;
    }
}


export async function sendAdminCancellationEmails(emails, cancelledDate, emailMessage) {
    try {
        const token = getToken();
        cancelledDate = moment(cancelledDate);

        const response = await axios.post(
            `${config.apiUrl}/view/reservations/send-cancellation-emails`,
            emails,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    cancelledDate: cancelledDate.format("YYYY-MM-DD"),
                    emailMessage: emailMessage,
                },
            }
        );
        return {success: true, data: response.data};
    } catch (error) {
        throw error;
    }
}

export async function fetchConfig() {
    try {
        const response = await axios.get(`${config.apiUrl}/config`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function fetchTournaments(status) {
    try {
        const response = await axios.get(`${config.apiUrl}/tournaments`, {
            params: {
                status: status // This will add a query parameter ?status=<status> to the URL
            }
        });
        return {success: true, data: response.data};
    } catch (error) {
        console.error("Error fetching tournaments: ", error);
        throw error;
    }
}

export async function fetchTournamentByFriendlyToken(token) {
    try {
        const response = await axios.get(`${config.apiUrl}/tournaments/friendly/${token}`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response?.data || error.message};
    }
}

export async function fetchAdminTournaments(status) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/supervisor/tournaments`, {
            params: { status: status },
            headers: { Authorization: `Bearer ${token}` }
        });
        return {success: true, data: response.data};
    } catch (error) {
        console.error("Error fetching tournaments: ", error);
        throw error;
    }
}


export async function fetchTournament(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/supervisor/tournaments/${tournamentId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function fetchParticipants(tournamentId) {
    try {
        const response = await axios.get(`${config.apiUrl}/tournaments/${tournamentId}/participants`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Error fetching tournaments"};
    }
}

export async function addNewOffer(newOffer) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/offers`, newOffer, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function updateOffer(offerId, offer) {
    try {
        const token = getToken();
        const response = await axios.patch(`${config.apiUrl}/offers/${offerId}`, offer, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function fetchOffers() {
    try {
        const token = getToken();
        const headers = token && isTokenValid(token) ? {Authorization: `Bearer ${token}`} : {};
        const response = await axios.get(`${config.apiUrl}/offers`, {headers});
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response?.data || "Unknown error"};
    }
}

export async function deleteOffer(id) {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/offers/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}


export async function addTournament(tournamentData) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/tournaments`, tournamentData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function updateTournament(id, tournamentData) {
    try {
        const token = getToken();
        const response = await axios.patch(`${config.apiUrl}/supervisor/tournaments/${id}`, tournamentData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function registerTournamentParticipant(tournamentId, userId, partnerEmail) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/tournaments/register`, {
            tournamentId: tournamentId,
            userId: userId,
            userEmail: "",
            partnerEmail: partnerEmail
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function registerTournamentParticipantBySupervisor(tournamentId, userEmail, partnerEmail) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/tournaments/register`, {
            tournamentId: tournamentId,
            userId: null,
            userEmail: userEmail,
            partnerEmail: partnerEmail
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function unRegisterTournamentParticipant(Id) {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/tournaments/register/${Id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function updateConfig(configurationData) {
    try {
        const token = getToken();
        const response = await axios.put(`${config.apiUrl}/super/config`, configurationData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function deleteTournament(id) {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/supervisor/tournaments/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response.data || "Unknown error"};
    }
}

export async function sendTournamentEmail(tournamentId, subject, message) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/tournaments/${tournamentId}/send-email`,
            { subject, message },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}


export async function startTournament(tournamentId, competitionSystem, seededPlayers) {
    try {
        const token = getToken();
        const requestBody = {
            competitionSystem,
            seededPlayers,
        };

        const response = await axios.post(`${config.apiUrl}/supervisor/tournaments/start/${tournamentId}`, requestBody, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export async function generateFinalMatches(tournamentId) {
    try {
        const response = await axios.post(`${config.apiUrl}/tournaments/generateFinalMatches/${tournamentId}`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export async function generateNextRound(tournamentId) {
    try {
        const response = await axios.post(`${config.apiUrl}/tournaments/generateNextRound/${tournamentId}`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export async function returnToPhaseGroup(tournamentId) {
    try {
        const response = await axios.delete(`${config.apiUrl}/tournaments/returnToPhaseGroup/${tournamentId}`);
        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export async function deleteMatches(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.delete(`${config.apiUrl}/supervisor/tournaments/deleteMatches/${tournamentId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export const fetchRoundRobinStandings = async (tournamentId) => {
    try {
        const response = await axios.get(`${config.apiUrl}/tournaments/${tournamentId}/standings`);
        return {success: true, data: response.data};
    } catch (error) {
        console.error('Failed to fetch tournament standings:', error);
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
};

export const fetchMatches = async (tournamentId) => {
    try {
        const response = await axios.get(`${config.apiUrl}/tournaments/${tournamentId}/matches`);
        return {success: true, data: response.data};
    } catch (error) {
        console.error('Failed to fetch matches:', error);
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export async function saveMatchResult(matchResult) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/tournaments/matches`, matchResult, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return {success: true, data: response.data};
    } catch (error) {
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export const fetchEloRanking = async (matchType, gender) => {
    try {
        const response = await fetch(`${config.apiUrl}/elo-ranking?matchType=${matchType}&gender=${gender}`);
        const data = await response.json();
        if (!Array.isArray(data)) {
            throw new Error('Data is not an array');
        }
        return data;
    } catch (error) {
        console.error("Failed to fetch elo ranking", error);
        throw error;
    }
}

export const fetchEloHistory = async (userId, matchType) => {
    try {
        const response = await axios.get(`${config.apiUrl}/elo-history/${userId}/${matchType}`);
        return {success: true, data: response.data};
    } catch (error) {
        console.error('Failed to fetch elo history:', error);
        return {success: false, data: error.response ? error.response.data : "Unknown error"};
    }
}

export const fetchMatchById = async (matchId) => {
    try {
        const response = await fetch(`${config.apiUrl}/matches/${matchId}`);
        const data = await response.json();
        return { success: response.ok, data };
    } catch (error) {
        console.error("Failed to fetch match by id:", error);
        return { success: false, data: null };
    }
};

// ---- LEAGUE API ----

export async function fetchLeagueView(tournamentId, roundNumber) {
    try {
        const params = roundNumber ? `?roundNumber=${roundNumber}` : '';
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${config.apiUrl}/leagues/${tournamentId}/view${params}`, { headers });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function updateLeagueGroupWhatsApp(tournamentId, groupId, whatsAppGroupUrl) {
    try {
        const token = getToken();
        const response = await axios.patch(
            `${config.apiUrl}/supervisor/leagues/${tournamentId}/groups/${groupId}/whatsapp-url`,
            { whatsAppGroupUrl },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchMyActivity(tournamentId, round = null) {
    try {
        const token = getToken();
        const params = round != null ? `?round=${round}` : '';
        const response = await axios.get(`${config.apiUrl}/leagues/${tournamentId}/my-activity${params}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchLeagueConfig(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/supervisor/leagues/${tournamentId}/config`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function saveLeagueConfig(tournamentId, configData) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/config`, configData, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function saveLeagueGroups(tournamentId, groups) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/groups`, groups, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function assignPlayersToGroups(tournamentId, roundNumber, assignments) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/groups/assign`,
            { roundNumber, assignments },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function startLeague(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/start`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function drawRandomPairs(tournamentId, seededParticipantIds) {
    try {
        const token = getToken();
        const response = await axios.post(
            `${config.apiUrl}/supervisor/leagues/${tournamentId}/random-pairs`,
            seededParticipantIds || [],
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data?.message || error.response?.data || error.message };
    }
}

export async function advanceLeagueRound(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/advance-round`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function rollbackLeagueRound(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/rollback-round`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function rollbackLeagueStart(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/rollback-start`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function changeLeagueRoundEndDate(tournamentId, newEndDate) {
    try {
        const token = getToken();
        const response = await axios.patch(`${config.apiUrl}/supervisor/leagues/${tournamentId}/round-end-date`,
            { newEndDate },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function changeLeagueRoundStartDate(tournamentId, newStartDate) {
    try {
        const token = getToken();
        const response = await axios.patch(`${config.apiUrl}/supervisor/leagues/${tournamentId}/round-start-date`,
            { newStartDate },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function suspendLeaguePlayer(tournamentId, participantId, fromRound) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/suspend-player`,
            { participantId, fromRound },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function addLeaguePlayer(tournamentId, participantId, groupLabel) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/add-player`,
            { participantId, groupLabel },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function modifyLeagueGroups(tournamentId, roundNumber, assignments) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/modify-groups`,
            { roundNumber, assignments },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function addLeaguePlayerToGroupWithMatches(tournamentId, participantId, groupLabel) {
    try {
        const token = getToken();
        const response = await axios.post(
            `${config.apiUrl}/supervisor/leagues/${tournamentId}/add-player-with-matches`,
            { participantId, groupLabel },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function removeLeaguePlayerFromGroup(tournamentId, participantId) {
    try {
        const token = getToken();
        const response = await axios.post(
            `${config.apiUrl}/supervisor/leagues/${tournamentId}/remove-player-from-group`,
            { participantId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function freezeLeagueParticipation(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/leagues/${tournamentId}/freeze`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function unfreezeLeagueParticipation(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/leagues/${tournamentId}/unfreeze`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function adminFreezePlayer(tournamentId, participantId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/freeze-player`,
            { participantId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function adminUnfreezePlayer(tournamentId, participantId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/unfreeze-player`,
            { participantId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchMatchProposals(tournamentId, roundNumber) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/leagues/${tournamentId}/proposals`, {
            params: { roundNumber },
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchMyMatchProposals(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/leagues/${tournamentId}/proposals/my`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function createMatchProposal(tournamentId, proposedDate, proposedFromTime, proposedToTime, confirmed = false) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/leagues/${tournamentId}/proposals`,
            { proposedDate, proposedStartTime: proposedFromTime, proposedToTime, confirmed: String(confirmed) },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function checkWindowAvailability(tournamentId, date, windowStart, windowEnd) {
    try {
        const token = getToken();
        const response = await axios.get(
            `${config.apiUrl}/leagues/${tournamentId}/proposals/window-availability`,
            { params: { date, windowStart, windowEnd }, headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, available: response.data.available };
    } catch (error) {
        return { success: false, available: false };
    }
}

export async function fetchAvailableCourtsForSlot(tournamentId, proposalId, slotStart) {
    try {
        const token = getToken();
        const response = await axios.get(
            `${config.apiUrl}/leagues/${tournamentId}/proposals/${proposalId}/slot-courts`,
            { params: { slotStart }, headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function acceptMatchProposal(tournamentId, proposalId, force = false, slots = null, confirmed = false) {
    try {
        const token = getToken();
        const query = force ? '?force=true' : '';
        const url = `${config.apiUrl}/leagues/${tournamentId}/proposals/${proposalId}/accept${query}`;
        const body = { ...(slots ? { slots } : {}), confirmed };
        const response = await axios.post(url, body,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchAvailableArrangementsForProposal(tournamentId, proposalId) {
    try {
        const token = getToken();
        const response = await axios.get(
            `${config.apiUrl}/leagues/${tournamentId}/proposals/${proposalId}/available-arrangements`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function cancelMatchProposal(tournamentId, proposalId, reason) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/leagues/${tournamentId}/proposals/${proposalId}/cancel`,
            { reason },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function checkCourtAvailability(tournamentId, date, startTime) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/leagues/${tournamentId}/proposals/availability`, {
            params: { date, startTime },
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function sendLeagueActivityReminders(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/send-reminders`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function runProposalDigest(tournamentId) {
    try {
        const token = getToken();
        const response = await axios.post(`${config.apiUrl}/supervisor/leagues/${tournamentId}/run-proposal-digest`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function recordPageView(path) {
    try {
        await axios.post(`${config.apiUrl}/page-views`, null, { params: { path } });
        return { success: true };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}

export async function fetchPageViewStats(path) {
    try {
        const token = getToken();
        const response = await axios.get(`${config.apiUrl}/super/page-views/stats`, {
            params: { path },
            headers: { Authorization: `Bearer ${token}` }
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, data: error.response?.data || error.message };
    }
}
